import type { UserStandard, UUID } from '@/shared/types/userStandard';

export interface UserStandardTreeNode extends UserStandard {
  children: UserStandardTreeNode[];
}

function compareNodes(a: UserStandardTreeNode, b: UserStandardTreeNode) {
  return (
    a.position - b.position || a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id)
  );
}

function sortNodes(nodes: UserStandardTreeNode[]) {
  nodes.sort(compareNodes);
  for (const node of nodes) sortNodes(node.children);
}

/**
 * Build the nested My Library tree from the flat `getAllUserStandards` rows. Sibling order mirrors
 * the API's `getUserStandardChildren` ordering (position, then created_at, then id). Rows whose
 * parent is missing from the set are treated as roots.
 */
export function buildUserStandardsTree(rows: UserStandard[]): UserStandardTreeNode[] {
  const byId = new Map<UUID, UserStandardTreeNode>();
  for (const row of rows) byId.set(row.id, { ...row, children: [] });

  const roots: UserStandardTreeNode[] = [];
  for (const node of byId.values()) {
    const parent = node.parent_id ? byId.get(node.parent_id) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }

  sortNodes(roots);
  return roots;
}

/**
 * Collections are the user's top-level folders. Loose root standards are not surfaced as
 * collections — the My Standards redesign keeps the root reserved for folders (see
 * standards-library-v2-my-standards-tree.md, decision A2).
 */
export function getCollections(roots: UserStandardTreeNode[]): UserStandardTreeNode[] {
  return roots.filter((node) => node.node_type === 'folder');
}

/**
 * Flat text search over the library. When `scope` is provided, only that collection's descendants
 * (not the collection node itself) are searched; otherwise the whole library is searched. Returns
 * leaf-shaped nodes (empty `children`) so the result list renders like a flattened tree.
 */
export function collectStandardMatches(
  rows: UserStandard[],
  query: string,
  scope?: UserStandardTreeNode | null
): UserStandardTreeNode[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];

  let pool = rows;
  if (scope) {
    const inScope = new Set<UUID>();
    const collect = (node: UserStandardTreeNode) => {
      for (const child of node.children) {
        inScope.add(child.id);
        collect(child);
      }
    };
    collect(scope);
    pool = rows.filter((row) => inScope.has(row.id));
  }

  return pool
    .filter((row) =>
      `${row.title} ${row.code ?? ''} ${row.description ?? ''}`.toLowerCase().includes(q)
    )
    .map<UserStandardTreeNode>((row) => ({ ...row, children: [] }));
}
